import Modal from '@sdk/components/ui/modal/Modal';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Button from '@sdk/components/ui/buttons/Button';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import gettext from '@sdk/lang/gettext';
import { GroupedPersonaPicker } from '@sdk/components/forms/personaPickers/GroupedPersonaPicker/GroupedPersonaPicker';
import TextInputBase from '@sdk/components/forms/TextInputBase';
import { FormActions, FormLabel, FormLayout } from '@sdk/components/ui/modalForm/modalForm.styles';

import ExclamationMarkIcon from '@web/assets/svg/UI/exclamation-mark.svg';
import NotesIcon from '@web/assets/svg/UI/notes.svg';
import RecipientsIcon from '@web/assets/svg/UI/personas.svg';
import TextEditor from '@web/richText/TextEditor';

import { useNewAnnouncementForm, usePersonaData } from './newAnnouncementForm.hooks';
import { IconWrapper } from './newAnnouncementForm.styles';

type Props = {
  onClose: () => void;
};

export const NewAnnouncementForm = ({ onClose }: Props) => {
  const { groupHeaders, mentionPersonas, pickerPersonas } = usePersonaData();
  const { fields, hasSubmitErrors, isActive, retardedSubmit } = useNewAnnouncementForm(onClose);

  return (
    <Modal isVisible onDismiss={onClose} fixed zIndex={1045}>
      <ModalBodyWithHeader
        width={620}
        titleThemeColor={ThemeColorType.primary}
        titleText={gettext('Send announcement')}
        titleIcon={
          <IconWrapper>
            <ExclamationMarkIcon aria-hidden={true} />
          </IconWrapper>
        }
        close={onClose}>
        <form>
          <FormLayout>
            <FormLabel>
              {gettext('Recipients')} <RecipientsIcon aria-hidden={true} />
            </FormLabel>
            <GroupedPersonaPicker
              groupHeadersDefinition={groupHeaders}
              hasError={hasSubmitErrors && fields.recipients.error?.length > 0}
              onSelectedItemsChange={fields.recipients.onChange}
              options={pickerPersonas}
              placeholder={gettext('Add users, positions, locations or groups')}
              selectedItems={fields.recipients.value}
              showSearchIcon={false}
            />

            <FormLabel htmlFor="new-announcement-title">
              {gettext('Title')} <ExclamationMarkIcon aria-hidden={true} />
            </FormLabel>
            <TextInputBase
              id="new-announcement-title"
              invalid={hasSubmitErrors && fields.title.error?.length > 0}
              maxLength={150}
              name="title"
              onChange={fields.title.onChange}
              onBlur={fields.title.onBlur}
              placeholder={gettext('Add title')}
              type="text"
              value={fields.title.value}
            />

            <FormLabel>
              {gettext('Content')} <NotesIcon aria-hidden={true} />
            </FormLabel>
            <TextEditor
              gifPickerEnabled={false}
              hasError={hasSubmitErrors && fields.content.error?.length > 0}
              onBlur={fields.content.onBlur}
              onContentChange={(content) => {
                fields.attachments.onChange(content.attachments);
                fields.content.onChange(content.text.trim());
              }}
              submitOnEnter={false}
              personas={mentionPersonas}
            />
          </FormLayout>
          <FormActions>
            <Button onClick={onClose}>{gettext('Cancel')}</Button>
            <Button
              inactive={!isActive}
              onClick={retardedSubmit}
              buttonType={isActive ? ButtonType.primary : ButtonType.disabledStyle}>
              {gettext('Send')}
            </Button>
          </FormActions>
        </form>
      </ModalBodyWithHeader>
    </Modal>
  );
};
